import * as React from "react";
import { cx, css } from "@emotion/css";
import breadcrumbSteps from "../support/breadcrumbSteps";
import bs from "../css/bootstrap.module.css";

const breadcrumbDesktop = css`
  display: block;
  @media (max-width: 991px) {
    display: none;
  }
`;
const breadcrumbMobile = css`
  display: none;
  height: 20px;
  margin: 0 22px 3em;
  position: relative;
  @media (max-width: 991px) {
    display: block;
  }
  .mobileBreadcrumbItem {
    width: 20px;
    height: 20px;
    border-radius: 15px;
    display: inline-block;
    border: 4px solid #8a9191;
    position: absolute;
    background: #fff;
    z-index: 2;
  }
  .mobileBreadcrumbProgressBar {
    height: 5px;
    z-index: 1;
    top: 7px;
    position: absolute;
    background: #8a9191;
  }
  .activeMobileStep {
    .mobileBreadcrumbItem {
      background: #063;
      border-color: #063;
    }
  }
  .completedMobileStep {
    .mobileBreadcrumbItem {
      background: #063;
      border-color: #063;
    }
    .mobileBreadcrumbProgressBar {
      background: #063;
      border-color: #063;
    }
  }
`;
const mobileBreadcrumbHeader = css`
  display: none;
  @media (max-width: 991px) {
    display: block;
    margin: 0 22px;
    font-size: 1.2em;
    color: #000;
    padding: 0.5em 0;
    display: block;
    text-align: center;
  }
`;
const breadcrumbStep = css`
  border-bottom: 3px solid #969696;
  color: #969696;
  margin-bottom: 25px;
  height: 35px;
  border-bottom-color: #666;
  color: #666;
  list-style-type: none;

  .breadcrumbStepNumber {
    display: inline-block;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 0.75em;
    font-weight: 700;
    line-height: 20px;
    color: #969696;
    text-align: center;
    border: 2px solid #969699;
  }
  .breadcrumbName {
    font-weight: 400;
    font-size: 1em;
    margin-left: 0.5em;
    line-height: 1.5em;
    height: 1.5em;
  }
`;

const activeStep = css`
  border-color: #063;
  color: #000;
  .breadcrumbStepNumber {
    background: #063;
    color: #fff;
    border-color: #063;
  }
`;
const completedStep = css`
  border-color: #063;
  color: #000;
  .breadcrumbStepNumber {
    background: #063;
    color: #fff;
    border-color: #063;
  }
`;

interface CustomProps {
  currentStep: number;
}

interface CustomMobileProps {
  currentStep: number;
  iteration: number;
}
interface CustomMobileProgressBarProps {
  currentStep: number;
  iteration: number;
}

const Breadcrumbs = ({ currentStep }: CustomProps): React.JSX.Element => {
  return (
    <div className={bs.row}>
      <div className={breadcrumbDesktop}>
        {breadcrumbSteps.map((breadcrumbItem, i) => (
          <div key={breadcrumbItem} className={bs["col-md-3"]}>
            <div
              className={cx(
                breadcrumbStep,
                currentStep === i + 1 ? activeStep : "",
                currentStep > i + 1 ? completedStep : ""
              )}
            >
              <span className="breadcrumbStepNumber">{i + 1}</span>
              <span className="breadcrumbName">{breadcrumbItem}</span>
            </div>
          </div>
        ))}
      </div>
      <div className={mobileBreadcrumbHeader}>
        Step {currentStep}: {breadcrumbSteps[currentStep - 1]}
      </div>
      <div className={breadcrumbMobile}>
        {breadcrumbSteps.map((breadcrumbItem, i) => (
          <div
            key={breadcrumbItem}
            className={cx(
              currentStep === i + 1 ? "activeMobileStep" : "",
              currentStep > i + 1 ? "completedMobileStep" : ""
            )}
          >
            <MobileBreadcrumbMarkers currentStep={currentStep} iteration={i} />
            <MobileBreadcrumbProgressBars
              currentStep={currentStep}
              iteration={i}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const MobileBreadcrumbMarkers = ({
  currentStep,
  iteration
}: CustomMobileProps): React.JSX.Element => {
  const breadcrumbLength = breadcrumbSteps.length;
  const currentIteration = iteration;
  let markerPosition = {};

  if (currentIteration === 0) {
    markerPosition = { left: "0" };
  } else if (currentIteration === breadcrumbLength - 1) {
    markerPosition = { right: "0" };
  } else {
    const tempPosition = Math.floor(
      (100 / (breadcrumbLength - 1)) * currentIteration
    );
    markerPosition = { left: `${String(tempPosition)}%` };
  }

  return <div className="mobileBreadcrumbItem" style={markerPosition} />;
};

const MobileBreadcrumbProgressBars = ({
  currentStep,
  iteration
}: CustomMobileProgressBarProps): React.JSX.Element => {
  const breadcrumbLength = breadcrumbSteps.length;
  const currentIteration = iteration;
  const widthOfProgressBar = Math.floor(100 / (breadcrumbLength - 1));

  let markerPosition = {};

  if (currentIteration === 0) {
    markerPosition = { left: "0", width: `${String(widthOfProgressBar)}%` };
  } else if (currentIteration !== breadcrumbLength - 1) {
    const tempPosition = Math.floor(
      (100 / (breadcrumbLength - 1)) * currentIteration
    );
    markerPosition = {
      left: `${String(tempPosition)}%`,
      width: `${String(widthOfProgressBar)}%`
    };
  }

  return <div className="mobileBreadcrumbProgressBar" style={markerPosition} />;
};

export default Breadcrumbs;
